import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { Modal } from "@/components/base/Modal";
import { canManageListings, isAdmin } from "@/lib/permissions";
import { getMarketplaceName } from "@/lib/providers/ebay-adapter";
import { connectorIsReady } from "@/lib/channel-coverage";
import { connectorDisplayName } from "@/lib/connector-identity";
import { parseListingDryRunResult, type ListingDryRunResult } from "@/lib/providers/listing-contract";
import { activeEbayPolicies, validateEbayPolicyChoices } from "@/lib/providers/ebay-active-policies";
import { requestPublication } from "@/lib/channels";
import EbayAspectTab from "@/pages/listings/detail/components/EbayAspectTab";

type HonestMode = "NOT_CONFIGURED" | "DRY_RUN" | "LIVE";
type EbaySubTab = "overview" | "aspects" | "policies" | "inventory_items" | "offers" | "orders";

function isEbaySubTab(value: string | null): value is EbaySubTab {
  return value === "overview"
    || value === "aspects"
    || value === "policies"
    || value === "inventory_items"
    || value === "offers"
    || value === "orders";
}

function HonestModeBadge({ mode }: { mode: HonestMode }) {
  const styles: Record<HonestMode, string> = {
    NOT_CONFIGURED: "bg-red-50 text-red-700 border border-red-200",
    DRY_RUN: "bg-amber-50 text-amber-700 border border-amber-200",
    LIVE: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${styles[mode]}`}>
      {mode === "NOT_CONFIGURED" ? "NOT CONFIGURED" : mode}
    </span>
  );
}

function syncStatusStyle(status: string): string {
  if (status === "synced") return "bg-emerald-50 text-emerald-700";
  if (status === "intended") return "bg-amber-50 text-amber-700";
  if (status === "error") return "bg-red-50 text-red-700";
  return "bg-slate-100 text-slate-500";
}

interface OfferPricingSummary {
  price?: { value?: string; currency?: string };
}

function formatOfferPrice(pricing: unknown): string {
  const summary = pricing as OfferPricingSummary | null | undefined;
  const price = summary?.price;
  if (!price?.value) return "—";
  return `${price.currency || ""} ${price.value}`.trim();
}

interface EbayTabProps {
  listingId: string;
  connectorId: string;
  orgId: string;
  productId: string;
  categoryId?: string | null;
  categoryName?: string | null;
}

interface EbayPublicationSelections {
  fulfillmentPolicyId: string;
  paymentPolicyId: string;
  returnPolicyId: string;
  merchantLocationKey: string;
}

interface EbayPublicationSettingsResponse {
  success: boolean;
  code?: string;
  error?: string;
  revision?: number;
  unchanged?: boolean;
  fulfillment_policy_id?: string;
  payment_policy_id?: string;
  return_policy_id?: string;
  merchant_location_key?: string;
}

type PolicySaveStatus = "loading" | "unsaved" | "saving" | "saved" | "error";

const EMPTY_SELECTIONS: EbayPublicationSelections = {
  fulfillmentPolicyId: "",
  paymentPolicyId: "",
  returnPolicyId: "",
  merchantLocationKey: "",
};

function selectionsFromResponse(data: EbayPublicationSettingsResponse): EbayPublicationSelections {
  return {
    fulfillmentPolicyId: data.fulfillment_policy_id || "",
    paymentPolicyId: data.payment_policy_id || "",
    returnPolicyId: data.return_policy_id || "",
    merchantLocationKey: data.merchant_location_key || "",
  };
}

function sameSelections(a: EbayPublicationSelections, b: EbayPublicationSelections): boolean {
  return a.fulfillmentPolicyId === b.fulfillmentPolicyId
    && a.paymentPolicyId === b.paymentPolicyId
    && a.returnPolicyId === b.returnPolicyId
    && a.merchantLocationKey === b.merchantLocationKey;
}

export default function EbayTab({ listingId, connectorId, orgId, productId, categoryId, categoryName }: EbayTabProps) {
  const { role } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeSubTab, setActiveSubTab] = useState<EbaySubTab>(() => {
    const requested = searchParams.get("ebayTab");
    return isEbaySubTab(requested) ? requested : "overview";
  });

  const selectEbaySubTab = (tab: EbaySubTab) => {
    setActiveSubTab(tab);
    const next = new URLSearchParams(searchParams);
    next.set("tab", "ebay");
    next.set("ebayTab", tab);
    setSearchParams(next);
  };

  useEffect(() => {
    const requested = searchParams.get("ebayTab");
    if (isEbaySubTab(requested)) setActiveSubTab(requested);
  }, [searchParams]);
  const [syncBusy, setSyncBusy] = useState(false);
  const [syncError, setSyncError] = useState("");
  const [reconBusy, setReconBusy] = useState(false);
  const [dryRunBusy, setDryRunBusy] = useState(false);
  const [dryRunResult, setDryRunResult] = useState<ListingDryRunResult | null>(null);
  const [planOpen, setPlanOpen] = useState(false);
  const [publishResult, setPublishResult] = useState<string>("");
  const settingsScope = `${orgId}:${listingId}:${connectorId}`;
  const [policySettings, setPolicySettings] = useState<{
    scope: string;
    draft: EbayPublicationSelections;
    saved: EbayPublicationSelections;
    revision: number;
    status: PolicySaveStatus;
    error: string;
    editSequence: number;
  }>({
    scope: settingsScope,
    draft: EMPTY_SELECTIONS,
    saved: EMPTY_SELECTIONS,
    revision: 0,
    status: "loading",
    error: "",
    editSequence: 0,
  });
  const canManage = canManageListings(role);
  const isAdminUser = isAdmin(role);

  useEffect(() => {
    setPolicySettings({
      scope: settingsScope,
      draft: EMPTY_SELECTIONS,
      saved: EMPTY_SELECTIONS,
      revision: 0,
      status: "loading",
      error: "",
      editSequence: 0,
    });
  }, [settingsScope]);

  const { data: persistedPolicySettings, error: persistedPolicySettingsError } = useQuery({
    queryKey: ["ebay-listing-publication-settings", orgId, listingId, connectorId],
    enabled: !!orgId && !!listingId && !!connectorId,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_ebay_listing_publication_settings", {
        p_org_id: orgId,
        p_listing_id: listingId,
        p_connector_id: connectorId,
      });
      if (error) throw error;
      const result = data as EbayPublicationSettingsResponse | null;
      if (!result?.success) throw new Error(result?.error || result?.code || "Unable to load saved eBay policies");
      return result;
    },
  });

  useEffect(() => {
    if (!persistedPolicySettings) return;
    const confirmed = selectionsFromResponse(persistedPolicySettings);
    setPolicySettings((current) => {
      if (current.scope !== settingsScope) return current;
      if (current.status === "saving" || !sameSelections(current.draft, current.saved)) return current;
      return {
        scope: settingsScope,
        draft: confirmed,
        saved: confirmed,
        revision: persistedPolicySettings.revision ?? 0,
        status: sameSelections(confirmed, EMPTY_SELECTIONS) ? "unsaved" : "saved",
        error: "",
        editSequence: current.editSequence,
      };
    });
  }, [persistedPolicySettings, settingsScope]);

  useEffect(() => {
    if (!persistedPolicySettingsError) return;
    setPolicySettings((current) => current.scope === settingsScope
      ? { ...current, status: "error", error: (persistedPolicySettingsError as Error).message }
      : current);
  }, [persistedPolicySettingsError, settingsScope]);

  const currentPolicySettings = policySettings.scope === settingsScope
    ? policySettings
    : { ...policySettings, draft: EMPTY_SELECTIONS, saved: EMPTY_SELECTIONS, status: "loading" as const };
  const {
    fulfillmentPolicyId,
    paymentPolicyId,
    returnPolicyId,
    merchantLocationKey,
  } = currentPolicySettings.draft;

  const editPolicySelection = (field: keyof EbayPublicationSelections, value: string) => {
    setPolicySettings((current) => {
      const base = current.scope === settingsScope
        ? current
        : { scope: settingsScope, draft: EMPTY_SELECTIONS, saved: EMPTY_SELECTIONS, revision: 0, status: "loading" as const, error: "", editSequence: 0 };
      const draft = { ...base.draft, [field]: value };
      return {
        ...base,
        draft,
        status: sameSelections(draft, base.saved) && !sameSelections(draft, EMPTY_SELECTIONS) ? "saved" : "unsaved",
        error: "",
        editSequence: base.editSequence + 1,
      };
    });
  };

  const { data: ebayConfig } = useQuery({
    queryKey: ["ebay-config", orgId, connectorId],
    enabled: !!orgId && !!connectorId,
    queryFn: async () => {
      const { data } = await supabase
        .from("connector_ebay_config")
        .select("*")
        .eq("connector_id", connectorId)
        .maybeSingle();
      return data;
    },
  });

  const { data: connector } = useQuery({
    queryKey: ["connector-status", orgId, connectorId],
    enabled: !!orgId && !!connectorId,
    queryFn: async () => {
      const { data } = await supabase
        .from("connectors")
        .select("id, auth_status, name")
        .eq("id", connectorId)
        .maybeSingle();
      return data;
    },
  });

  const { data: inventoryItems } = useQuery({
    queryKey: ["ebay-inventory-items", orgId, listingId],
    enabled: !!orgId && !!listingId,
    queryFn: async () => {
      const { data } = await supabase
        .from("ebay_inventory_items")
        .select("*")
        .eq("listing_id", listingId)
        .order("sku");
      return (data ?? []) as Array<Record<string, unknown>>;
    },
  });

  const { data: itemGroup } = useQuery({
    queryKey: ["ebay-item-group", orgId, listingId],
    enabled: !!orgId && !!listingId,
    queryFn: async () => {
      const { data } = await supabase
        .from("ebay_item_groups")
        .select("*")
        .eq("listing_id", listingId)
        .maybeSingle();
      return data;
    },
  });

  const { data: offers } = useQuery({
    queryKey: ["ebay-offers", orgId, listingId],
    enabled: !!orgId && !!listingId,
    queryFn: async () => {
      const { data } = await supabase
        .from("ebay_offers")
        .select("*")
        .eq("listing_id", listingId)
        .order("sku");
      return (data ?? []) as Array<Record<string, unknown>>;
    },
  });

  const { data: orderMappings } = useQuery({
    queryKey: ["ebay-listing-order-mappings", orgId, connectorId, listingId, productId],
    enabled: !!orgId && !!connectorId && !!listingId && !!productId,
    queryFn: async () => {
      const { data: productLines, error: productLinesError } = await supabase
        .from("sales_order_items")
        .select("sales_order_id")
        .eq("org_id", orgId)
        .eq("product_id", productId);
      if (productLinesError) throw productLinesError;

      const salesOrderIds = Array.from(new Set(
        (productLines ?? [])
          .map((line) => line.sales_order_id as string | null)
          .filter((id): id is string => Boolean(id)),
      ));
      if (salesOrderIds.length === 0) return [] as Array<Record<string, unknown>>;

      const { data, error } = await supabase
        .from("ebay_order_mappings")
        .select("*")
        .eq("org_id", orgId)
        .eq("connector_id", connectorId)
        .in("sales_order_id", salesOrderIds)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as Array<Record<string, unknown>>;
    },
  });

  const policyMarketplace = ebayConfig?.marketplace_id || "EBAY_AU";
  const { data: businessPolicies, error: policiesError, isFetching: policiesFetching } = useQuery({
    // Keep the invalidation prefix used by connector sync, but do not reuse its
    // differently shaped cache entry (or an old unfiltered listing result).
    queryKey: ["ebay-policies", orgId, connectorId, "active", policyMarketplace],
    enabled: !!orgId && !!connectorId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ebay_business_policies")
        .select("id, org_id, connector_id, marketplace_id, policy_type, policy_name, external_policy_id, is_default, is_active")
        .eq("connector_id", connectorId)
        .eq("org_id", orgId)
        .eq("marketplace_id", policyMarketplace)
        .eq("is_active", true);
      if (error) throw error;
      return activeEbayPolicies(data, { orgId, connectorId, marketplaceId: policyMarketplace });
    },
  });

  const { data: inventoryLocations } = useQuery({
    queryKey: ["ebay-locations", orgId, connectorId],
    enabled: !!orgId && !!connectorId,
    queryFn: async () => {
      const { data } = await supabase
        .from("ebay_inventory_locations")
        .select("id, merchant_location_key, location_name")
        .eq("connector_id", connectorId)
        .eq("org_id", orgId)
        .eq("is_active", true);
      return (data ?? []) as Array<{
        id: string;
        merchant_location_key: string;
        location_name: string | null;
      }>;
    },
  });

  const activePolicies = policiesError ? [] : activeEbayPolicies(businessPolicies, {
    orgId, connectorId, marketplaceId: policyMarketplace,
  });
  const fulfillmentPolicies = activePolicies.filter((p) => p.policy_type === "fulfillment");
  const paymentPolicies = activePolicies.filter((p) => p.policy_type === "payment");
  const returnPolicies = activePolicies.filter((p) => p.policy_type === "return");

  const policyValidation = validateEbayPolicyChoices(activePolicies, {
    fulfillment: fulfillmentPolicyId,
    payment: paymentPolicyId,
    return: returnPolicyId,
  });
  const selectedLocationAvailable = (inventoryLocations ?? []).some((location) => location.merchant_location_key === merchantLocationKey);
  const completePolicyDraft = Boolean(fulfillmentPolicyId && paymentPolicyId && returnPolicyId && merchantLocationKey);
  const policySettingsDirty = !sameSelections(currentPolicySettings.draft, currentPolicySettings.saved);
  const savedSelectionsReady = currentPolicySettings.status !== "loading"
    && currentPolicySettings.status !== "error"
    && currentPolicySettings.status !== "saving"
    && !policySettingsDirty
    && completePolicyDraft
    && policyValidation.ok
    && selectedLocationAvailable;

  const catalogueBody = (mode: "dry_run" | "LIVE") => ({
    listing_id: listingId,
    org_id: orgId,
    connector_id: connectorId,
    mode,
    publication_authority: "normal",
    idempotency_key: `${orgId}:${connectorId}:${listingId}:publish`,
  });

  const handleSavePolicySettings = async () => {
    if (!canManage || currentPolicySettings.status === "saving" || currentPolicySettings.status === "loading") return;
    if (!completePolicyDraft) {
      setPolicySettings((current) => current.scope === settingsScope
        ? { ...current, status: "error", error: "Select all three policies and a merchant location before saving." }
        : current);
      return;
    }
    if (!policyValidation.ok || !selectedLocationAvailable) {
      setPolicySettings((current) => current.scope === settingsScope
        ? { ...current, status: "error", error: policyValidation.ok ? "The selected merchant location is no longer active." : policyValidation.message }
        : current);
      return;
    }

    const saveScope = settingsScope;
    const saveSequence = currentPolicySettings.editSequence;
    const draft = { ...currentPolicySettings.draft };
    const expectedRevision = currentPolicySettings.revision;
    setPolicySettings((current) => current.scope === saveScope ? { ...current, status: "saving", error: "" } : current);

    try {
      const { data, error } = await supabase.rpc("save_ebay_listing_publication_settings", {
        p_org_id: orgId,
        p_listing_id: listingId,
        p_connector_id: connectorId,
        p_fulfillment_policy_id: draft.fulfillmentPolicyId,
        p_payment_policy_id: draft.paymentPolicyId,
        p_return_policy_id: draft.returnPolicyId,
        p_merchant_location_key: draft.merchantLocationKey,
        p_expected_revision: expectedRevision,
      });
      if (error) throw error;
      const result = data as EbayPublicationSettingsResponse | null;
      if (!result?.success) {
        setPolicySettings((current) => {
          if (current.scope !== saveScope) return current;
          const conflictSaved = result?.code === "SETTINGS_CONFLICT" ? selectionsFromResponse(result) : current.saved;
          return {
            ...current,
            saved: conflictSaved,
            revision: result?.code === "SETTINGS_CONFLICT" ? (result.revision ?? current.revision) : current.revision,
            status: "error",
            error: result?.error || result?.code || "Unable to save eBay policies",
          };
        });
        return;
      }

      const confirmed = selectionsFromResponse(result);
      setPolicySettings((current) => {
        if (current.scope !== saveScope) return current;
        const editedWhileSaving = current.editSequence !== saveSequence;
        return {
          ...current,
          draft: editedWhileSaving ? current.draft : confirmed,
          saved: confirmed,
          revision: result.revision ?? current.revision,
          status: editedWhileSaving ? "unsaved" : "saved",
          error: "",
        };
      });
      queryClient.setQueryData(
        ["ebay-listing-publication-settings", orgId, listingId, connectorId],
        result,
      );
      queryClient.invalidateQueries({ queryKey: ["listing-detail", orgId, listingId] });
      queryClient.invalidateQueries({ queryKey: ["listings-index", orgId] });
    } catch (err) {
      setPolicySettings((current) => current.scope === saveScope
        ? { ...current, status: "error", error: (err as Error).message || "Unable to save eBay policies" }
        : current);
    }
  };

  // Determine honest mode from the actual OAuth connector state.
  // app_credentials_status is readiness metadata only; server-side eBay app
  // credentials are authoritative and are checked by ebay-token-refresh.
  const isConnected = connectorIsReady(connector?.auth_status);
  const honestMode: HonestMode = isConnected ? "LIVE" : "DRY_RUN";

  const handleDryRun = async () => {
    setSyncError("");
    if (policiesFetching || policiesError) {
      setSyncError(policiesFetching ? "Wait for the current eBay policies to finish loading." : "Unable to load current eBay policies. Retry before validating.");
      return;
    }
    if (!savedSelectionsReady) {
      const blockers = policySettingsDirty
        ? [{ code: "EBAY_SETTINGS_UNSAVED", message: "Save the changed policies and merchant location before validating." }]
        : !selectedLocationAvailable
          ? [{ code: "MERCHANT_LOCATION_UNAVAILABLE", message: "The saved merchant location is no longer active." }]
          : policyValidation.issues;
      setDryRunResult({ success: false, mode: "DRY_RUN", message: "Save current policies and location for this listing before validating.", blockers });
      selectEbaySubTab("policies");
      setPlanOpen(true);
      return;
    }
    setDryRunBusy(true);
    try {
      const res = await supabase.functions.invoke("ebay-catalogue-publish", {
        body: catalogueBody("dry_run"),
      });
      let rawResult: unknown = res.data;
      if (!rawResult && res.error) {
        const context = (res.error as { context?: Response }).context;
        if (context) {
          try {
            rawResult = await context.clone().json();
          } catch {
            // Fall back to the transport-level message below.
          }
        }
        rawResult ??= { success: false, mode: "DRY_RUN", error: res.error.message };
      }
      setDryRunResult(parseListingDryRunResult(rawResult));
      setPlanOpen(true);
    } catch (err) {
      setSyncError((err as Error).message);
    }
    setDryRunBusy(false);
  };

  const handleLivePublish = async () => {
    if (honestMode !== "LIVE") {
      setSyncError(`Cannot publish in ${honestMode} mode. Ensure connector is connected and credentials configured.`);
      return;
    }
    if (policiesFetching || policiesError) {
      setSyncError("Current eBay policies must load successfully before publishing.");
      return;
    }
    if (!savedSelectionsReady) {
      setSyncError(policySettingsDirty
        ? "Save the changed policies and merchant location before publishing."
        : !selectedLocationAvailable
          ? "The saved merchant location is no longer active. Select and save an active mapping."
          : policyValidation.message);
      selectEbaySubTab("policies");
      return;
    }
    setSyncBusy(true);
    setSyncError("");
    setPublishResult("");
    try {
      const result = await requestPublication(orgId, listingId);
      if (!result.success) throw new Error(result.error || "Publication request failed");
      setPublishResult("Publication queued. Provider progress is tracked durably and will recover if this page closes.");
      queryClient.invalidateQueries({ queryKey: ["ebay-inventory-items", orgId, listingId] });
      queryClient.invalidateQueries({ queryKey: ["ebay-offers", orgId, listingId] });
      queryClient.invalidateQueries({ queryKey: ["ebay-item-group", orgId, listingId] });
      queryClient.invalidateQueries({ queryKey: ["listing-detail", orgId, listingId] });
    } catch (err) {
      setSyncError((err as Error).message);
    }
    setSyncBusy(false);
  };

  const handleReconcile = async () => {
    setReconBusy(true);
    try {
      await supabase.functions.invoke("ebay-reconciliation", {
        body: { listing_id: listingId, org_id: orgId, mode: honestMode === "LIVE" ? "LIVE" : "dry_run" },
      });
      queryClient.invalidateQueries({ queryKey: ["ebay-inventory-items", orgId, listingId] });
    } catch (err) {
      setSyncError((err as Error).message);
    }
    setReconBusy(false);
  };

  return (
    <div className="space-y-4">
      {/* Honest Mode Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <HonestModeBadge mode={honestMode} />
        {honestMode === "DRY_RUN" && (
          <span className="text-xs text-amber-600">Connector not authorized. DRY RUN available — no live API calls.</span>
        )}
        {honestMode === "LIVE" && (
          <span className="text-xs text-emerald-600">Connected. Live publishing available.</span>
        )}
      </div>

      {/* eBay Sub-navigation */}
      <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg p-1 w-fit flex-wrap">
        {[
          { key: "overview" as const, label: "eBay Overview", icon: "ri-store-2-line" },
          { key: "aspects" as const, label: "Aspects", icon: "ri-list-settings-line" },
          { key: "policies" as const, label: "Policies", icon: "ri-shield-line" },
          { key: "inventory_items" as const, label: `Items (${inventoryItems?.length || 0})`, icon: "ri-archive-2-line" },
          { key: "offers" as const, label: `Offers (${offers?.length || 0})`, icon: "ri-price-tag-3-line" },
          { key: "orders" as const, label: `Orders (${orderMappings?.length || 0})`, icon: "ri-file-list-line" },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => selectEbaySubTab(t.key)}
            className={`px-3 py-1.5 text-xs font-medium rounded-md whitespace-nowrap flex items-center gap-1 transition-colors ${
              activeSubTab === t.key ? "bg-emerald-50 text-emerald-700" : "text-slate-600 hover:bg-slate-50"
            }`}
          >
            <i className={t.icon} />
            {t.label}
          </button>
        ))}
      </div>

      {syncError && (
        <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{syncError}</div>
      )}

      {activeSubTab === "overview" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* eBay Listing Info */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="text-sm font-semibold text-slate-800 mb-3">eBay Listing Info</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">Mode</span>
                <HonestModeBadge mode={honestMode} />
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Marketplace</span>
                <span className="text-slate-800">{getMarketplaceName(ebayConfig?.marketplace_id || "EBAY_AU")}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Currency</span>
                <span className="text-slate-800">{ebayConfig?.currency || "AUD"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Connector</span>
                <span className="text-slate-800">{connectorDisplayName({
                  id: connectorId,
                  name: connector?.name ?? "",
                  provider: "ebay",
                  authStatus: connector?.auth_status,
                })}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Category</span>
                <span className="text-slate-800">{categoryName || categoryId || "Not set"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Inventory Items</span>
                <span className="text-slate-800">{inventoryItems?.length || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Offers</span>
                <span className="text-slate-800">{offers?.length || 0}</span>
              </div>
            </div>

            {publishResult && (
              <div className="mt-3 px-3 py-2.5 bg-emerald-50 border border-emerald-100 rounded-lg text-sm text-emerald-800">{publishResult}</div>
            )}
            {canManage && (
              <div className="flex items-center gap-2 mt-4 flex-wrap">
                {/* Validate / Dry Run button — always available */}
                <button
                  onClick={handleDryRun}
                  disabled={dryRunBusy || policiesFetching}
                  className="px-3 py-1.5 bg-amber-50 border border-amber-200 hover:bg-amber-100 disabled:opacity-60 text-amber-700 text-xs font-medium rounded-lg whitespace-nowrap"
                  data-testid="ebay-validate-dry-run"
                  title="Validate the listing without publishing to eBay"
                >
                  {dryRunBusy ? "Validating..." : "Validate / Dry Run"}
                </button>

                {/* LIVE Publish — only available when fully connected */}
                <button
                  onClick={handleLivePublish}
                  disabled={syncBusy || policiesFetching || honestMode !== "LIVE"}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg whitespace-nowrap ${
                    honestMode === "LIVE"
                      ? "bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white"
                      : "bg-slate-100 text-slate-400 cursor-not-allowed"
                  }`}
                  title={honestMode !== "LIVE" ? `Requires LIVE mode (currently ${honestMode})` : "Publish to eBay"}
                >
                  {syncBusy ? "Publishing..." : "Publish to eBay"}
                </button>

                {/* Reconcile — live mode only */}
                <button
                  onClick={handleReconcile}
                  disabled={reconBusy}
                  className="px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-medium rounded-lg whitespace-nowrap"
                >
                  {reconBusy ? "Reconciling..." : honestMode === "LIVE" ? "Reconcile" : "Check Internal Drift"}
                </button>
              </div>
            )}
          </div>

          {/* Item Group */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="text-sm font-semibold text-slate-800 mb-3">Item Group (Variation Parent)</h3>
            {itemGroup ? (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Key</span>
                  <span className="text-slate-800 font-mono text-xs">{itemGroup.external_item_group_key || "—"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Provider Confirmed</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${itemGroup.provider_confirmed_at ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                    {itemGroup.provider_confirmed_at ? new Date(itemGroup.provider_confirmed_at as string).toLocaleDateString() : "Not confirmed"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Sync Status</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${syncStatusStyle(itemGroup.sync_status as string)}`}>
                    {itemGroup.sync_status}
                  </span>
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-400">No item group yet. Created on first LIVE publish for multi-variation products.</p>
            )}
          </div>
        </div>
      )}

      {activeSubTab === "aspects" && (
        <EbayAspectTab
          listingId={listingId}
          orgId={orgId}
          connectorId={connectorId}
          categoryId={categoryId}
        />
      )}

      {activeSubTab === "policies" && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="text-sm font-semibold text-slate-800 mb-3">eBay Business Policies</h3>
          <p className="text-sm text-slate-500 mb-4">
            Only active policies for this connector and marketplace are available.
            Save postage, payment, return and warehouse choices together before validating or publishing.
          </p>
          {policiesError && <p role="alert" className="text-sm text-red-600 mb-3">Unable to load current eBay policies. Retry before selecting or validating.</p>}
          {persistedPolicySettingsError && <p role="alert" className="text-sm text-red-600 mb-3">Unable to load this listing&apos;s saved policies: {(persistedPolicySettingsError as Error).message}</p>}
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Fulfillment policy (this connector)</label>
              <select
                data-testid="ebay-fulfillment-policy"
                value={fulfillmentPolicyId}
                onChange={(e) => editPolicySelection("fulfillmentPolicyId", e.target.value)}
                disabled={!canManage || currentPolicySettings.status === "loading"}
                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm"
              >
                <option value="">Select fulfillment policy…</option>
                {fulfillmentPolicyId && !fulfillmentPolicies.some((p) => p.external_policy_id === fulfillmentPolicyId) && (
                  <option value={fulfillmentPolicyId} disabled>Saved policy unavailable — {fulfillmentPolicyId}</option>
                )}
                {fulfillmentPolicies.map((p) => (
                  <option key={p.external_policy_id} value={p.external_policy_id}>{p.policy_name || p.external_policy_id}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Payment policy (this connector)</label>
              <select
                data-testid="ebay-payment-policy"
                value={paymentPolicyId}
                onChange={(e) => editPolicySelection("paymentPolicyId", e.target.value)}
                disabled={!canManage || currentPolicySettings.status === "loading"}
                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm"
              >
                <option value="">Select payment policy…</option>
                {paymentPolicyId && !paymentPolicies.some((p) => p.external_policy_id === paymentPolicyId) && (
                  <option value={paymentPolicyId} disabled>Saved policy unavailable — {paymentPolicyId}</option>
                )}
                {paymentPolicies.map((p) => (
                  <option key={p.external_policy_id} value={p.external_policy_id}>{p.policy_name || p.external_policy_id}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Return policy (this connector)</label>
              <select
                data-testid="ebay-return-policy"
                value={returnPolicyId}
                onChange={(e) => editPolicySelection("returnPolicyId", e.target.value)}
                disabled={!canManage || currentPolicySettings.status === "loading"}
                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm"
              >
                <option value="">Select return policy…</option>
                {returnPolicyId && !returnPolicies.some((p) => p.external_policy_id === returnPolicyId) && (
                  <option value={returnPolicyId} disabled>Saved policy unavailable — {returnPolicyId}</option>
                )}
                {returnPolicies.map((p) => (
                  <option key={p.external_policy_id} value={p.external_policy_id}>{p.policy_name || p.external_policy_id}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Merchant location (this connector)</label>
              <select
                data-testid="ebay-merchant-location"
                value={merchantLocationKey}
                onChange={(e) => editPolicySelection("merchantLocationKey", e.target.value)}
                disabled={!canManage || currentPolicySettings.status === "loading"}
                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm"
              >
                <option value="">Select location…</option>
                {merchantLocationKey && !(inventoryLocations ?? []).some((location) => location.merchant_location_key === merchantLocationKey) && (
                  <option value={merchantLocationKey} disabled>Saved location unavailable — {merchantLocationKey}</option>
                )}
                {(inventoryLocations ?? []).map((l) => (
                  <option key={l.merchant_location_key} value={l.merchant_location_key}>{l.location_name || l.merchant_location_key}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-3 pt-2">
              <button
                type="button"
                data-testid="ebay-save-policy-settings"
                onClick={handleSavePolicySettings}
                disabled={!canManage
                  || currentPolicySettings.status === "loading"
                  || currentPolicySettings.status === "saving"
                  || !policySettingsDirty
                  || !completePolicyDraft
                  || !policyValidation.ok
                  || !selectedLocationAvailable}
                className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {currentPolicySettings.status === "saving" ? "Saving…" : "Save policies & location"}
              </button>
              <span
                data-testid="ebay-policy-save-status"
                role={currentPolicySettings.status === "error" ? "alert" : "status"}
                className={`text-sm ${
                  currentPolicySettings.status === "saved"
                    ? "text-emerald-700"
                    : currentPolicySettings.status === "error"
                      ? "text-red-700"
                      : "text-amber-700"
                }`}
              >
                {currentPolicySettings.status === "loading" && "Loading saved choices…"}
                {currentPolicySettings.status === "unsaved" && "Unsaved"}
                {currentPolicySettings.status === "saving" && "Saving"}
                {currentPolicySettings.status === "saved" && "Saved"}
                {currentPolicySettings.status === "error" && `Save failed: ${currentPolicySettings.error}`}
              </span>
            </div>
          </div>
        </div>
      )}

      {activeSubTab === "inventory_items" && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-800">eBay Inventory Items</h3>
            <p className="text-xs text-slate-400 mt-1">
              Items with <strong>intended</strong> sync status have been validated but not yet confirmed by eBay. Items with <strong>synced</strong> status are provider-confirmed.
            </p>
          </div>
          {!inventoryItems || inventoryItems.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-400">No inventory items created yet. They are created automatically during a successful LIVE publish; there is no separate manual item-creation step.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs text-slate-500">
                  <tr>
                    <th className="px-4 py-3 font-medium">SKU</th>
                    <th className="px-4 py-3 font-medium">External Key</th>
                    <th className="px-4 py-3 font-medium">Sync Status</th>
                    <th className="px-4 py-3 font-medium">Provider Confirmed</th>
                    <th className="px-4 py-3 font-medium">Last Synced</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {inventoryItems.map((item) => (
                    <tr key={item.id as string}>
                      <td className="px-4 py-3 font-mono text-xs text-slate-700">{item.sku as string}</td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-400">{item.external_inventory_item_key as string || "—"}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-1.5 py-0.5 rounded-full ${syncStatusStyle(item.sync_status as string)}`}>
                          {item.sync_status as string}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {item.provider_confirmed_at ? (
                          <span className="text-xs text-emerald-600">{new Date(item.provider_confirmed_at as string).toLocaleString()}</span>
                        ) : (
                          <span className="text-xs text-slate-400">Not confirmed</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-400">
                        {item.last_synced_at ? new Date(item.last_synced_at as string).toLocaleString() : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeSubTab === "offers" && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-800">eBay Offers</h3>
          </div>
          {!offers || offers.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-400">No offers created yet. Offers are created automatically during a successful LIVE publish; resolve any publication error and retry rather than creating offers manually.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs text-slate-500">
                  <tr>
                    <th className="px-4 py-3 font-medium">SKU</th>
                    <th className="px-4 py-3 font-medium">Offer ID</th>
                    <th className="px-4 py-3 font-medium">Listing ID</th>
                    <th className="px-4 py-3 font-medium">Price</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Provider Confirmed</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {offers.map((offer) => (
                    <tr key={offer.id as string}>
                      <td className="px-4 py-3 font-mono text-xs text-slate-700">{offer.sku as string}</td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-400">{offer.external_offer_id as string || "—"}</td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-400">{offer.external_listing_id as string || "—"}</td>
                      <td className="px-4 py-3 text-sm text-slate-700">
                        {formatOfferPrice(offer.pricing_summary)}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-1.5 py-0.5 rounded-full ${syncStatusStyle(offer.sync_status as string)}`}>
                          {offer.offer_status as string || offer.sync_status as string}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {offer.provider_confirmed_at ? (
                          <span className="text-xs text-emerald-600">{new Date(offer.provider_confirmed_at as string).toLocaleDateString()}</span>
                        ) : (
                          <span className="text-xs text-slate-400">Not confirmed</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeSubTab === "orders" && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-800">eBay Order Mappings</h3>
          </div>
          {!orderMappings || orderMappings.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-400">No eBay orders linked to this product on this account.</div>
          ) : (
            <div className="divide-y divide-slate-50">
              {orderMappings.map((order) => (
                <div key={order.id as string} className="px-4 py-3 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-800">{order.external_order_id as string}</p>
                    <p className="text-xs text-slate-400">
                      {order.buyer_username as string || "Buyer (privacy protected)"}
                    </p>
                    {order.sales_order_id && (
                      <Link
                        to={`/sales-orders/${order.sales_order_id as string}`}
                        className="text-xs text-emerald-600 hover:text-emerald-700 underline underline-offset-2"
                      >
                        Linked to Sales Order
                      </Link>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${order.payment_status === "PAID" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                      {order.payment_status as string || order.order_status as string || "unknown"}
                    </span>
                    <span className="text-xs text-slate-400">{new Date(order.created_at as string).toLocaleDateString()}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* DRY RUN result modal */}
      <Modal
        open={planOpen}
        onClose={() => setPlanOpen(false)}
        title="Validate / Dry Run Result"
        footer={
          <button onClick={() => setPlanOpen(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">Close</button>
        }
      >
        {dryRunResult && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <HonestModeBadge mode={dryRunResult.mode} />
              <span className="text-sm text-slate-600">{dryRunResult.message || dryRunResult.error || ""}</span>
            </div>

            {(dryRunResult.plan?.steps.length ?? 0) > 0 && (
              <div>
                <p className="text-xs font-medium text-slate-500 mb-2">Publication Plan:</p>
                <div className="space-y-1">
                  {dryRunResult.plan!.steps.map((step, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-slate-600">
                      <span className="w-5 h-5 flex items-center justify-center bg-slate-100 rounded-full text-slate-500 text-xs font-medium flex-shrink-0">{step.step}</span>
                      <span className={step.skipped ? "text-slate-300 line-through" : ""}>{step.name}</span>
                      {step.count != null && <span className="text-slate-400">({step.count})</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {dryRunResult.intended_items && (
              <div>
                <p className="text-xs font-medium text-slate-500 mb-1">
                  Intended Items: {dryRunResult.intended_items.length} ·
                  Intended Offers: {dryRunResult.intended_offers?.length || 0}
                </p>
                <p className="text-xs text-amber-600">
                  Note: These are DRY RUN intentions only. No external IDs were created. No provider-confirmed state was set.
                </p>
              </div>
            )}

            {(dryRunResult.plan?.validation_issues.length ?? 0) > 0 && (
              <div className="bg-red-50 border border-red-100 rounded-lg p-3">
                <p className="text-xs font-medium text-red-700 mb-1">Validation Issues:</p>
                {dryRunResult.plan!.validation_issues.map((issue, i) => (
                  <p key={i} className="text-xs text-red-600">{issue.code}: {issue.message}</p>
                ))}
              </div>
            )}

            {(dryRunResult.blockers?.length ?? 0) > 0 && (
              <div className="bg-red-50 border border-red-100 rounded-lg p-3">
                <p className="text-xs font-medium text-red-700 mb-1">Blocking Issues:</p>
                {dryRunResult.blockers!.map((b, i) => (
                  <p key={i} className="text-xs text-red-600">{b.code}: {b.message}</p>
                ))}
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
